import { z } from 'zod';

// IDs ------------------------------------------------------------------------

/**
 * Coerce IDs (number | bigint) a string. Algunos endpoints del backend
 * devuelven `id: 1` mientras otros usan cuids string; frontend los maneja
 * uniformemente como string. El preprocess solo se aplica si el valor no es
 * null/undefined, para que `.nullable()` y `.optional()` sigan funcionando.
 */
export const IdSchema = z.preprocess((v) => {
  if (v === null || v === undefined) return v;
  if (typeof v === 'number' || typeof v === 'bigint') return String(v);
  return v;
}, z.string().min(1));
export type Id = z.infer<typeof IdSchema>;

/**
 * String que puede faltar/null en el backend. Default `''`. Usar SOLO en read
 * schemas; los Create/Update deben quedar strict.
 */
export const softString = z
  .string()
  .nullable()
  .optional()
  .default('')
  .transform((v) => v ?? '');

/**
 * Array de strings que puede faltar. Default `[]`.
 */
export const softStringArray = z
  .array(z.string())
  .nullable()
  .optional()
  .default([])
  .transform((v) => v ?? []);

/**
 * Boolean tolerante. Default `true` (en listings asumimos activo). Sobrescribir
 * con `.default(false)` cuando el contexto pida lo contrario.
 */
export const softBoolean = z
  .boolean()
  .nullable()
  .optional()
  .default(true)
  .transform((v) => v ?? true);

/**
 * Boolean para query string / body serializado como string.
 *
 * `z.coerce.boolean()` es una TRAMPA: `Boolean("false") === true`, y también
 * `Boolean("0")`. Con coerción cualquier string no vacío parsea a `true`, así
 * que `?isActive=false` termina como `true` y el filtro queda invertido.
 *
 * Este helper preprocesa: acepta boolean nativo, o strings `"true"`/`"1"` →
 * true y `"false"`/`"0"` → false. Cualquier otro string falla la validación.
 *
 * Espeja `booleanQueryParam` del back (packages/common) para que el mismo
 * URL param se interprete igual en front y back.
 */
export const booleanQueryParam = z.preprocess((v) => {
  if (typeof v === 'boolean') return v;
  if (v === 'true' || v === '1') return true;
  if (v === 'false' || v === '0') return false;
  return v;
}, z.boolean());

// Enums compartidos (espejo de Prisma backend) -------------------------------

export const MediaTypeSchema = z.enum(['image', 'video', 'document', 'other']);
export type MediaType = z.infer<typeof MediaTypeSchema>;

export const ClientTypeSchema = z.enum(['INTERNAL', 'EXTERNAL']);
export type ClientType = z.infer<typeof ClientTypeSchema>;

export const WebhookStatusSchema = z.enum(['sent', 'error', 'skipped']);
export type WebhookStatus = z.infer<typeof WebhookStatusSchema>;

// Estados de una sugerencia IA según backend:
// queued → processing → pending → (accepted | rejected | modified | failed).
// `failed` cuando el LLM no pudo producir respuesta válida.
export const AiSuggestionStatusSchema = z.enum([
  'queued',
  'processing',
  'pending',
  'failed',
  'accepted',
  'rejected',
  'modified',
]);
export type AiSuggestionStatus = z.infer<typeof AiSuggestionStatusSchema>;

export const IngestEventStatusSchema = z.enum(['pending', 'processing', 'success', 'failed']);
export type IngestEventStatus = z.infer<typeof IngestEventStatusSchema>;

export const ProductChangeTypeSchema = z.enum(['create', 'update', 'delete']);
export type ProductChangeType = z.infer<typeof ProductChangeTypeSchema>;

// Paginación ------------------------------------------------------------------

export const PaginationParamsSchema = z.object({
  skip: z.coerce.number().int().min(0).default(0),
  take: z.coerce.number().int().min(1).max(200).default(50),
});
export type PaginationParams = z.infer<typeof PaginationParamsSchema>;

export const paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    data: z.array(item),
    total: z.number().int().nonnegative(),
    skip: z.number().int().nonnegative(),
    // take puede ser 0 cuando el array viene vacío y el envelope reconstruye
    // el shape paginated con `array.length` como fallback.
    take: z.number().int().nonnegative(),
    // Campos opcionales que algunos endpoints (ej: GET /products multi-source)
    // exponen en meta.pagination del envelope. Pasan por el adapter.
    limitClamped: z.boolean().optional(),
    appliedSlugs: z.array(z.string()).optional(),
  });
export type Paginated<T> = {
  data: T[];
  total: number;
  skip: number;
  take: number;
};

// Error envelope (NestJS) -----------------------------------------------------

// Back nuevo (Zod via `nestjs-zod`) emite `path`; back legacy emite `field`.
// Aceptamos ambos y normalizamos a `field` para que el resto del front no
// tenga que branchear.
// `row`, `column`, `value` son campos opcionales del importer 422 (release
// 2026-09-02, hub-service post-4f95976): cada bad cell del XLSX viene con
// row (1-indexed, header = 1) + column NAME + valor tipeado por el admin.
// Sin default `strip`, Zod los eliminaría silenciosamente y el drawer del
// import job perdería la ubicación. Preservamos con optional y transform.
export const ApiErrorDetailSchema = z
  .object({
    field: z.string().optional(),
    path: z.string().optional(),
    message: z.string(),
    code: z.string().optional(),
    row: z.number().int().optional(),
    column: z.string().optional(),
    value: z.string().optional(),
  })
  .transform((d) => ({
    field: d.field ?? d.path,
    message: d.message,
    code: d.code,
    row: d.row,
    column: d.column,
    value: d.value,
  }));
export type ApiErrorDetail = z.infer<typeof ApiErrorDetailSchema>;

// El back tiene tres shapes de error dependiendo del origen:
// - NestJS estándar: `{ statusCode, message, error, details? }` con
//   `error` = nombre corto ('Bad Request'), `details` = ARRAY.
// - Business errors (release 08-25+): `{ statusCode, message, type, details? }`
//   con `type` = tipo de dominio ('BUSINESS_VALIDATION') y `details` =
//   OBJECT `{ path: message }` (todos los valores son string).
// - Structured details (release 09-25+): `{ statusCode, message, details? }`
//   con `details` = OBJECT con valores no-string (ej: 409 collision devuelve
//   `{ conflictingCategoryId: 518, conflictingCategoryName: 'Piñon' }`).
//   El front expone esto como `structuredDetails` para consumers que quieran
//   accionar sobre esa info (ej: link "Ver categoría existente").
// Aceptamos los tres y normalizamos.
export const ApiErrorSchema = z
  .object({
    statusCode: z.number().int(),
    message: z.union([z.string(), z.array(z.string())]),
    error: z.string().optional(),
    // `type` es alias del back para business errors — lo mergemos con `error`.
    type: z.string().optional(),
    // `details` puede venir como array (NestJS clásico), object mapa
    // path→message (business errors) u object con valores estructurados
    // (409 conflicts, etc). Tolerantes a los tres.
    details: z
      .union([
        z.array(ApiErrorDetailSchema),
        z.record(z.string(), z.string()),
        z.record(z.string(), z.unknown()),
      ])
      .optional(),
  })
  .transform((raw) => {
    let details: ApiErrorDetail[] | undefined;
    let structuredDetails: Record<string, unknown> | undefined;
    if (Array.isArray(raw.details)) {
      // Arm 1: array clásico — ya viene normalizado por ApiErrorDetailSchema.
      details = raw.details;
    } else if (raw.details !== undefined) {
      const values = Object.values(raw.details);
      const allString = values.every((v) => typeof v === 'string');
      if (allString) {
        // Arm 2: business error path→message. Normalizamos a array.
        details = Object.entries(raw.details).map(([field, message]) => ({
          field,
          message: message as string,
          code: undefined,
          row: undefined,
          column: undefined,
          value: undefined,
        }));
      } else {
        // Arm 3: structured details (valores no-string). Preservamos el raw
        // object para que el consumer pueda leer campos tipo
        // `conflictingCategoryId`.
        structuredDetails = raw.details as Record<string, unknown>;
      }
    }
    return {
      statusCode: raw.statusCode,
      message: raw.message,
      // Normalizamos business `type` a `error` para que el front tenga un solo
      // campo que branchear.
      error: raw.error ?? raw.type,
      details,
      structuredDetails,
    };
  });
export type ApiError = z.infer<typeof ApiErrorSchema>;

// Audit fields comunes (timestamps) ------------------------------------------

export const TimestampsSchema = z.object({
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
});

// Auth response ---------------------------------------------------------------

// Una app del menú de apps de Effetá (`apps` del refresh). Solo direcciones
// `https://`: van en un enlace y en una imagen.
const httpsUrl = z.url({ protocol: /^https$/ });

// Los íconos los sirve Effetá Apps (`auth.effeta.online`, contrato del back):
// la imagen solo se pide a un dominio de Effetá. Un ícono de otro lado cae a
// la inicial sin perder la app. La dirección de la app no se limita: una app
// de la suite puede vivir en otro dominio.
const effetaIconUrl = z.url({ protocol: /^https$/, hostname: /^([a-z0-9-]+\.)*effeta\.online$/ });

export const EffetaAppSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  iconUrl: effetaIconUrl.nullable().catch(null),
  url: httpsUrl,
  current: z.boolean(),
});
export type EffetaApp = z.infer<typeof EffetaAppSchema>;

// Response de `POST /auth/refresh`: el pase de Effetá y los segundos que le
// quedan (900 o menos). El front lo usa para programar la renovación.
//
// `apps` es el menú de apps de la persona y puede faltar. Un `apps` que falta
// o que no se entiende NUNCA hace fallar el refresh: queda `undefined`, igual
// que si no hubiera venido.
export const RefreshResponseSchema = z.object({
  access_token: z.string().min(1),
  expires_in: z.number().int().positive(),
  apps: z.array(EffetaAppSchema).optional().catch(undefined),
});
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;

// Helpers ---------------------------------------------------------------------

/**
 * Acepta campos nuevos sin romper. Útil cuando el backend evoluciona
 * más rápido que el contrato del frontend.
 */
export const passthrough = <T extends z.ZodObject<z.ZodRawShape>>(s: T) => s.passthrough();
